import React, { useEffect, useState } from 'react';
import { Col, Empty, Row, Select, Spin, Card, Typography, DatePicker, Button, Dropdown, Menu } from 'antd';
import moment from 'moment';
import { DownloadOutlined, FileTextOutlined } from '@ant-design/icons';
import PageLayout from '../../SharedComponents/PageLayout';
import Summery from '../../SharedComponents/AnalyticsCard/Summary';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchAnalyticsData,
  fetchAnalyticsRouteData,
  getRoutesAnalyticsData,
  getRoutesAnalyticsTableData,
  fetchVehicleServiceKpi,
  getVehicleServiceKpi,
} from '../../store/analytics.store';
import { getRoutesLoading } from '../../store/trips.store';
import { getUserAccountForCustomers, getUserAccountForCustomersLoading, getUserType } from '../../store/auth.store';
import DeliveryReport from '../../SharedComponents/Reports/DeliveryReport';
import MileageReport from '../../SharedComponents/Reports/MileageReport';
import AnalyticsSummaryReport from '../../SharedComponents/Reports/AnalyticsSummaryReport';

const { Option } = Select;
const { Text } = Typography;

const Analytics = (): JSX.Element => {
  const [startDate, setStartDate] = useState<moment.Moment | null>(null);
  const [endDate, setEndDate] = useState<moment.Moment | null>(null);
  const [selectedUser, setSelectedUser] = useState('all');
  const dispatch = useAppDispatch();
  const data = useAppSelector(getRoutesAnalyticsData);
  const tableData = useAppSelector(getRoutesAnalyticsTableData);
  const loading = useAppSelector(getRoutesLoading);
  const userType = useAppSelector(getUserType);
  const userNamesLoading = useAppSelector(getUserAccountForCustomersLoading);
  const userNames = useAppSelector(getUserAccountForCustomers);
  const vehicleServiceKpi = useAppSelector(getVehicleServiceKpi);
  const isCustomer = userType === 'customer';
  const isUser = userType === 'user';

  useEffect(() => {
    if (!userType) return;
    dispatch(fetchVehicleServiceKpi());
    dispatch(fetchAnalyticsData({ username: '' }));
    dispatch(fetchAnalyticsRouteData({}));
  }, [userType]);

  useEffect(() => {
    if (selectedUser) {
      dispatch(
        fetchAnalyticsData({
          username: selectedUser,
          start_date: startDate ? startDate.format('YYYY-MM-DD') : undefined,
          end_date: endDate ? endDate.format('YYYY-MM-DD') : undefined,
        }),
      );

      dispatch(
        fetchAnalyticsRouteData({
          start_date: startDate ? startDate.format('YYYY-MM-DD') : undefined,
          end_date: endDate ? endDate.format('YYYY-MM-DD') : undefined,
        }),
      );
    }
  }, [selectedUser, startDate, endDate]);

  return (
    <PageLayout
      title="Analytics Dashboard"
      actions={[
        data?.metrics && tableData?.metrics && (
          <Dropdown
            key="reports"
            overlay={
              <Menu>
                <Menu.Item key="delivery" icon={<FileTextOutlined />}>
                  <DeliveryReport
                    routes={tableData.routes}
                    totalPlanned={tableData.metrics.total_planned_deliveries}
                    totalActual={tableData.metrics.total_actual_deliveries}
                    totalCancelled={tableData.metrics.total_cancelled_deliveries}
                    totalSkipped={tableData.metrics.total_skipped_deliveries}
                    successRate={tableData.metrics.delivery_success_rate}
                    totalActualDistance={data.metrics.total_actual_distance}
                    dateRange={{ start: startDate?.format('YYYY-MM-DD'), end: endDate?.format('YYYY-MM-DD') }}
                  />
                </Menu.Item>
                <Menu.Item key="mileage" icon={<FileTextOutlined />}>
                  <MileageReport
                    routes={tableData.routes}
                    totalDistance={data.metrics.total_distance}
                    optimizedDistance={data.metrics.optimized_distance}
                    totalActualDistance={data.metrics.total_actual_distance}
                    dateRange={{ start: startDate?.format('YYYY-MM-DD'), end: endDate?.format('YYYY-MM-DD') }}
                  />
                </Menu.Item>
                <Menu.Item key="summary" icon={<FileTextOutlined />}>
                  <AnalyticsSummaryReport
                    metrics={data.metrics}
                    locations={data.locations}
                    routeOverviewMetrics={tableData.metrics}
                    vehicleServiceKpi={vehicleServiceKpi}
                    routes={tableData.routes}
                    dateRange={{ start: startDate?.format('YYYY-MM-DD'), end: endDate?.format('YYYY-MM-DD') }}
                  />
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
          >
            <Button type="primary" icon={<DownloadOutlined />}>
              Download Reports
            </Button>
          </Dropdown>
        ),
      ]}
    >
      <Card className="mg-b-20 bg-slate h-80 br-8 shadow-md" bordered={false}>
        <Row justify="space-between" align="middle" className="w-100" wrap={false}>
          <Col flex="auto">
            <Row gutter={32} align="middle">
              <Col>
                <div className="d-flex ai-center ws-nowrap">
                  <Text strong className="white-text mg-r-10">
                    Start Date:{' '}
                  </Text>
                  <DatePicker
                    value={startDate}
                    onChange={(date) => setStartDate(date)}
                    format="YYYY-MM-DD"
                    placeholder="Select Start Date"
                    className="custom-select w-160"
                    disabledDate={(current: moment.Moment | null): boolean => {
                      if (!current) return false;
                      return current.endOf('day').isAfter(moment().endOf('day'));
                    }}
                  />
                </div>
              </Col>
              <Col>
                <div className="d-flex ai-center ws-nowrap">
                  <Text strong className="white-text mg-r-10">
                    End Date:{' '}
                  </Text>
                  <DatePicker
                    value={endDate}
                    onChange={(date) => setEndDate(date)}
                    format="YYYY-MM-DD"
                    placeholder="Select End Date"
                    className="custom-select w-160"
                    disabledDate={(current: moment.Moment | null): boolean => {
                      if (!current) return false;
                      const isAfterToday = current.endOf('day').isAfter(moment().endOf('day'));
                      const isBeforeStartDate = startDate ? current.isBefore(startDate.startOf('day')) : false;
                      return isAfterToday || isBeforeStartDate;
                    }}
                  />
                </div>
              </Col>
            </Row>
          </Col>
          {isUser && (
            <Col flex="300px">
              <div className="d-flex ai-center ws-nowrap jc-end">
                <Text strong className="white-text mg-r-10">
                  Select User:{' '}
                </Text>
                <Select
                  loading={userNamesLoading}
                  className="w-160"
                  placeholder="Search user"
                  value={selectedUser}
                  onChange={(value) => setSelectedUser(value)}
                >
                  <Option key="all" value="all">
                    All Users
                  </Option>
                  {userNames.map(({ name, username }) => (
                    <Option key={username} value={username}>
                      {name}
                    </Option>
                  ))}
                </Select>
              </div>
            </Col>
          )}
        </Row>
      </Card>

      <Spin spinning={loading} size="large">
        <Row gutter={[16, 16]} justify="center">
          {isCustomer && selectedUser === '' && (
            <Col span={24}>
              <Empty description="Select a user to view analytics." />
            </Col>
          )}
          {!loading && data?.metrics && tableData?.metrics && (
            <Col span={24}>
              <Summery
                data={data.metrics}
                locations={data.locations}
                routeOverviewMetrics={tableData.metrics}
                routes={tableData.routes}
                vehicleServiceKpi={vehicleServiceKpi}
              />
            </Col>
          )}
        </Row>
      </Spin>
    </PageLayout>
  );
};

export default Analytics;
